import { apiUrl } from "@/api/api";
import { parse } from "date-fns";
import { useState } from "react";
import { ContentReviewModal } from "@/components/ContentReviewModal";
import { ContentToReview } from "@/api/content-review";
import { DialogTrigger } from "@/components/ui/dialog";
import type { Persona } from "@/api/personas";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

import { LoadingButton } from "@/components/LoadingButton";
import { User, UserIcon } from "lucide-react";
import { Rating } from "@/components/ui/rating";
import { PersonaImageAvatar } from "./PersonaImageAvatar";
import { getModelLabel } from "@/config/models";

const formatDateString = (dateString: string) => {
  const parsedDate = parse(dateString, "yyyy-MM-dd-HH:mm:ss", new Date());
  return parsedDate.toLocaleDateString();
};

export function ContentReviewCard({
  item,
  persona,
  viewMode,
  onDelete,
  onApprove,
  onRate,
}: {
  item: ContentToReview;
  persona: Persona;
  viewMode: "video" | "image";
  onDelete: () => Promise<void>;
  onApprove: () => Promise<void>;
  onRate: (value: number | null) => void;
}) {
  const [loading, setLoading] = useState<
    "delete" | "approve" | "upload" | null
  >(null);

  const approved = item.status === "approved";
  const published = item.status === "published";

  // Original image logic
  const imageUrl = item.optimized_url ? item.optimized_url : item.thumbnail_url;
  const imageSrc = `${apiUrl}/get-file?url=${encodeURIComponent(imageUrl)}`;
  const videoSrc = item.video_url
    ? `${apiUrl}/get-file?url=${encodeURIComponent(item.video_url)}`
    : undefined;

  // We pass both videoSrc and imageSrc into the modal item to ensure it can show whichever is present
  const modalItems = [
    {
      videoSrc,
      imageSrc,
      shareText: item.image_caption,
      prompt: item.prompt,
      model: getModelLabel(item.model),
      docId: item.doc_id,
      rating: item.rating,
      approved,
      published,
    },
  ];

  return (
    <Card
      className="bg-black/40 border-0 text-white transition-all duration-200 hover:transform hover:scale-[1.02] hover:shadow-xl hover:shadow-black/20"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "auto",
        backgroundColor: "rgba(0,0,0,0.40)",
        color: "white",
        maxWidth: 250,
      }}
    >
      <CardContent className="px-3 mt-3">
        <ContentReviewModal
          persona={persona}
          items={modalItems}
          // No `_idx` needed if we only use the second param or none
          onRate={async (_, newValue) => {
            onRate(newValue);
          }}
          onApprove={async () => {
            setLoading("approve");
            await onApprove();
            setLoading(null);
          }}
          onDelete={async () => {
            setLoading("delete");
            await onDelete();
            setLoading(null);
          }}
        >
          <DialogTrigger asChild>
            <div>
              <div className="relative max-w-[270px] aspect-[3/2] rounded-2xl border border-white/10">
                {item.video_url && viewMode === "video" ? (
                  <video
                    className="cursor-pointer h-[150px]"
                    autoPlay
                    muted
                    loop
                    src={videoSrc}
                    style={{
                      borderRadius: 6,
                      backgroundColor: "#f5ebff",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    className={`cursor-pointer rounded-md ${item.origin === "uploaded" ? "object-contain" : "object-cover"} h-[150px] w-full`}
                    src={`${apiUrl}/get-file?url=${encodeURIComponent(
                      item.thumbnail_url,
                    )}`}
                    data-testid="content-review-card-image"
                    alt={item.image_caption}
                  />
                )}
                {item.thumbnail_url?.includes("uploaded") && (
                  <User className="absolute top-2 right-2 w-4 h-4 text-black pointer-events-none rounded-full ring-2 ring-white bg-white/70 backdrop-blur-sm" />
                )}
              </div>
            </div>
          </DialogTrigger>
        </ContentReviewModal>

        <div className="flex flex-row items-center justify-between mt-2">
          <div className="flex flex-row gap-2 items-center">
            <PersonaImageAvatar
              className="h-8 w-8"
              altText={persona?.name}
              src={
                persona.avatar_id
                  ? `${apiUrl}/get-file?url=persona-knowledge-base/${persona.persona_id}/${persona.avatar_id}`
                  : undefined
              }
              gender={persona?.gender}
            />
            <div className="flex flex-col">
              <p className="text-base leading-5">
                {persona.name || "Unknown Agent"}
              </p>
              <p className="text-xs text-gray-400">
                {formatDateString(item.timestamp)}
              </p>
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center">
            {approved && <p className="text-xs text-green-500/80">Approved</p>}
            {published && <p className="text-xs text-green-500/80">Posted</p>}
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-4 w-full items-center">
          <div className="w-8 flex justify-center">
            <UserIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Rating
            value={(item.rating ?? 0) / 2}
            max={5}
            precision={0.5}
            onChange={(val) => onRate((val ?? 0) * 2)}
          />
        </div>
      </CardContent>

      <CardFooter className="pb-2">
        <div className="flex flex-row gap-2 w-full items-center justify-center">
          <LoadingButton
            className="flex-1 h-8 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
            variant="ghost"
            disabled={loading != null}
            loading={loading === "delete"}
            onClick={() => {
              setLoading("delete");
              onDelete().finally(() => setLoading(null));
            }}
          >
            Delete
          </LoadingButton>
          <LoadingButton
            className="flex-1 h-8 text-xs"
            variant="default"
            disabled={approved || loading != null}
            loading={loading === "approve"}
            onClick={() => {
              setLoading("approve");
              onApprove().finally(() => setLoading(null));
            }}
          >
            Approve
          </LoadingButton>
        </div>
      </CardFooter>
    </Card>
  );
}
